import { BufferGeometry, CanvasTexture, DoubleSide, Float32BufferAttribute, LinearFilter, Mesh, MeshStandardMaterial, SRGBColorSpace, type Object3D } from 'three';
import type { FrameObject } from './FrameObject';
import { fontStack, type EngraveFont } from '../engrave/fonts';
import { inkById } from '../engrave/inks';
import { canvasMeasure, displayText, sharedMeasureContext } from '../engrave/layout';
import { LOGO_PATHS, LOGO_VIEWBOX } from '../engrave/logo';
import { slotFace, slotSide, type EngraveState, type FreePlace, type SlotKey } from '../engrave/state';

/**
 * A201 (David, 8 octobre : « la gravure visible en 3D ») — the engraving on the 3D temples of the try-on: each view
 * (the Maison Mata logo, the texts) as a thin decal lying on its face of the temple, in its colour, typeface, letter
 * height, place and tilt — the same millimetres as the cards and the workshop sheet.
 *
 * Which temple: the cards draw each view as it is seen when looking at that face (the photo's hinge on the right,
 * mirrored for « left temple · inside » and « right temple · outside »). In the frame's 3D space (front +Z, temples
 * towards −Z, `temple_L` at +X), the face drawn with its hinge on the right is the inside of `temple_L` and the
 * outside of `temple_R`: the « B2 · Branche droite » views go on `temple_L`, the « B1 · Branche gauche » views on
 * `temple_R`. Reading direction along the temple follows from where the viewer stands (see `READING`).
 *
 * The preview is mirrored like a mirror: with `mirrored`, the letters are flipped so that they read the right way
 * on the screen (and on the order photo, mirrored the same way).
 */
export interface TempleEngravingInput {
  state: EngraveState;
  font: EngraveFont;
  /** Centre of each view (mm from the hinge, across from the middle line, tilt), as the cards place it. */
  places: Partial<Record<SlotKey, FreePlace>>;
  /** Capital height (mm) of each view, as engraved on this model. */
  caps: Partial<Record<SlotKey, number>>;
}

interface EngraveGeom {
  lengthMm: number;
  widthMm: number;
  edgeMm: number;
  tipRatio: number;
  outline: Array<[number, number]>;
}

/** Temple mesh of each side of the cards. */
const MESH_OF: Record<'l' | 'r', string> = { l: 'temple_R', r: 'temple_L' };
/** Reading direction along the temple's own x (backwards from the hinge) for each view. */
const READING: Record<SlotKey, 1 | -1> = { ir: -1, er: 1, il: 1, el: -1 };
/** Decal resolution and its distance off the acetate. */
const PX_PER_MM = 22;
const LIFT_MM = 0.035;
const SEGMENTS = 10;

/** Top and bottom of the temple outline at `x` (mm from the hinge). */
export function outlineSpan(outline: ReadonlyArray<[number, number]>, x: number): { top: number; bottom: number } | null {
  let top = -Infinity, bottom = Infinity;
  for (let i = 0; i < outline.length; i++) {
    const [x1, y1] = outline[i]!;
    const [x2, y2] = outline[(i + 1) % outline.length]!;
    if ((x1 <= x && x <= x2) || (x2 <= x && x <= x1)) {
      const y = x1 === x2 ? Math.max(y1, y2) : y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
      const yLow = x1 === x2 ? Math.min(y1, y2) : y;
      top = Math.max(top, y); bottom = Math.min(bottom, yLow);
    }
  }
  return Number.isFinite(top) && Number.isFinite(bottom) ? { top, bottom } : null;
}

/** z of the temple's outer (`outer`) or inner face at `x`, in the temple's own coordinates (see taperTemple). */
export function faceZ(g: Pick<EngraveGeom, 'lengthMm' | 'widthMm' | 'edgeMm' | 'tipRatio'>, x: number, outer: boolean): number {
  if (!outer) return -g.edgeMm;
  const t = Math.min(1, Math.max(0, x / g.lengthMm));
  return -g.edgeMm + g.widthMm * (1 + (g.tipRatio - 1) * t);
}

/** The view drawn on a canvas, white on transparent (the material gives the ink colour); its size in mm. */
function drawView(slot: SlotKey, input: TempleEngravingInput, capMm: number, flip: boolean): { canvas: HTMLCanvasElement; wMm: number; hMm: number } | null {
  const cap = capMm * PX_PER_MM;
  const canvas = document.createElement('canvas');
  let ctx: CanvasRenderingContext2D | null;
  if (slot === 'il') {
    const vb = LOGO_VIEWBOX.split(' ').map(Number) as [number, number, number, number];
    const h = cap, w = (h * vb[2]) / vb[3];
    const pad = Math.ceil(h * 0.3);
    canvas.width = Math.ceil(w + 2 * pad); canvas.height = Math.ceil(h + 2 * pad);
    ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (flip) { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
    ctx.translate(pad, pad);
    ctx.scale(h / vb[3], h / vb[3]);
    ctx.translate(-vb[0], -vb[1]);
    ctx.fillStyle = '#ffffff';
    for (const d of LOGO_PATHS) ctx.fill(new Path2D(d), 'evenodd');
  } else {
    const text = input.state.texts[slot];
    if (!text) return null;
    const shown = displayText(text, input.font);
    const metrics = canvasMeasure(sharedMeasureContext(), input.font);
    const fontSize = cap / metrics.capRatio;
    const width = metrics.measure(shown, fontSize);
    const pad = Math.ceil(fontSize * 0.35);
    canvas.width = Math.ceil(width + 2 * pad); canvas.height = Math.ceil(fontSize * 1.5 + 2 * pad);
    ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (flip) { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
    ctx.font = `${input.font.weight} ${fontSize.toFixed(2)}px ${fontStack(input.font)}`;
    const c = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
    if ('letterSpacing' in c) c.letterSpacing = `${(input.font.letterSpacing * fontSize).toFixed(2)}px`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#ffffff';
    // Capitals centred on the canvas (the place is the centre of the capitals).
    ctx.fillText(shown, canvas.width / 2, canvas.height / 2 + cap / 2);
  }
  return { canvas, wMm: canvas.width / PX_PER_MM, hMm: canvas.height / PX_PER_MM };
}

/** A strip lying on the face: `SEGMENTS` quads along the text, each vertex on the (tapered) face. */
function decalGeometry(g: EngraveGeom, slot: SlotKey, place: FreePlace, wMm: number, hMm: number): BufferGeometry | null {
  const span = outlineSpan(g.outline, place.x);
  if (!span) return null;
  const outer = slotFace(slot) === 'ext';
  const dir = READING[slot];
  const cy = (span.top + span.bottom) / 2 - place.y; // + across = lower as seen
  const rad = (place.rot * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const positions: number[] = [];
  const uvs: number[] = [];
  for (let i = 0; i <= SEGMENTS; i++) {
    const a = (i / SEGMENTS - 0.5) * wMm;
    for (const [j, b] of [[0, -hMm / 2], [1, hMm / 2]] as const) {
      // Viewer frame: right = dir·x, up = y; clockwise tilt as seen.
      const x = place.x + dir * (a * cos + b * sin);
      const y = cy - a * sin + b * cos;
      const z = faceZ(g, x, outer) + (outer ? LIFT_MM : -LIFT_MM);
      positions.push(x, y, z);
      uvs.push(i / SEGMENTS, j);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < SEGMENTS; i++) {
    const p = i * 2;
    index.push(p, p + 2, p + 1, p + 1, p + 2, p + 3);
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

export class TempleEngraving {
  private decals: Mesh[] = [];
  private frame: FrameObject | null = null;
  /** What the decals on the frame show (nothing is rebuilt while it stays the same). */
  private key = '';

  /** Puts the engraving on `frame`'s temples (null input or frame: removes it). */
  update(frame: FrameObject | null, input: TempleEngravingInput | null, mirrored: boolean): void {
    const fonts = typeof document !== 'undefined' ? (document as Document & { fonts?: FontFaceSet }).fonts : undefined;
    // The typeface counts once it is loaded (the letters were drawn in the fallback before).
    const ready = input && fonts ? fonts.check(`${input.font.weight} 20px ${fontStack(input.font)}`) : true;
    const key = frame && input ? JSON.stringify([frame.spec.id, input.state, input.font.id, input.places, input.caps, mirrored, ready]) : '';
    if (frame === this.frame && key === this.key && (key === '' || this.decals.every(m => m.parent))) return;
    this.clear();
    this.frame = frame;
    this.key = key;
    if (!frame || !input) return;
    for (const slot of ['il', 'ir', 'el', 'er'] as const) {
      const place = input.places[slot];
      const cap = input.caps[slot];
      if (!place || !cap) continue;
      const temple = frame.model.getObjectByName(MESH_OF[slotSide(slot)]) as Object3D | undefined;
      const geom = temple?.userData.engraveGeom as EngraveGeom | undefined;
      if (!temple || !geom) continue;
      const drawn = drawView(slot, input, cap, mirrored);
      if (!drawn) continue;
      const geo = decalGeometry(geom, slot, place, drawn.wMm, drawn.hMm);
      if (!geo) continue;
      const texture = new CanvasTexture(drawn.canvas);
      texture.colorSpace = SRGBColorSpace;
      texture.minFilter = LinearFilter;
      texture.generateMipmaps = false;
      const ink = inkById(input.state.inks[slot]);
      const metal = ink.id === 'or' || ink.id === 'argent';
      const material = new MeshStandardMaterial({
        map: texture, color: ink.color, transparent: true, depthWrite: false, side: DoubleSide,
        roughness: metal ? 0.32 : 0.55, metalness: metal ? 0.55 : 0,
        polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
      });
      // The fade (FrameObject.setOpacity) keeps it transparent at full opacity.
      material.userData.baseOpacity = 1;
      material.userData.baseTransparent = true;
      const mesh = new Mesh(geo, material);
      mesh.name = `engraving_${slot}`;
      mesh.renderOrder = 3;
      mesh.frustumCulled = false;
      mesh.userData.engraving = slot;
      temple.add(mesh);
      this.decals.push(mesh);
    }
    // New materials: the fade and the temple cut pick them up.
    frame.invalidateOpacity();
  }

  /** The decals now on the frame (tests, QA). */
  get meshes(): readonly Mesh[] { return this.decals; }

  clear(): void {
    for (const mesh of this.decals) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      const m = mesh.material as MeshStandardMaterial;
      m.map?.dispose();
      m.dispose();
    }
    if (this.decals.length) this.frame?.invalidateOpacity();
    this.decals = [];
  }

  dispose(): void {
    this.clear();
    this.frame = null;
    this.key = '';
  }
}
